import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInitials, shouldShowApiToast } from '../../utils/helpers';
import { useModal } from '../../context/ModalContext';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getGroups,
  getGroup,
  createGroup,
  updateGroup,
  deleteGroup,
  toggleGroupStatus,
  duplicateGroup,
  getGroupMembers,
  addMembersToGroup,
  removeMemberFromGroup,
  sendSmsToGroup,
  exportGroups,
  getGroupStats,
  clearApiMessage,
  clearError,
  clearGroupState,
  selectGroups,
  selectGroupPagination,
  selectCurrentGroup,
  selectGroupMembers,
  selectGroupMembersPagination,
  selectGroupStats,
  selectGroupsLoading,
  selectGroupsApiMessage,
  selectGroupsError,
  selectGroupStatsSummary,
} from '../../store/slices/reports/groupSlice';

import { getMembers, selectMembers, selectMembersLoading } from '../../store/slices/main/memberSlice';
import { Users, Search, Pencil, Save, Download, BarChart3, ClipboardList, TrendingUp, FileText, Trash2, AlertTriangle, Send, Lightbulb, CheckCircle2, Trophy, Calendar, Eye, Plus, Copy, Loader2 } from 'lucide-react';

const GroupsManager = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [availableMembers, setAvailableMembers] = useState([]);
  const [addMembersModalOpen, setAddMembersModalOpen] = useState(false);
  const [addMembersGroup, setAddMembersGroup] = useState(null);
  const [createLeaderSearch, setCreateLeaderSearch] = useState('');
  const [selectedCreateLeader, setSelectedCreateLeader] = useState(null);
  const [showCreateLeaderDropdown, setShowCreateLeaderDropdown] = useState(false);
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [editLeaderSearch, setEditLeaderSearch] = useState('');
  const [selectedEditLeader, setSelectedEditLeader] = useState(null);
  const [showEditLeaderDropdown, setShowEditLeaderDropdown] = useState(false);
  const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // ==================== REFS ====================
  const editNameRef = useRef(null);
  const editDescRef = useRef(null);
  const editIconRef = useRef(null);
  const editLeaderRef = useRef(null);
  const editActiveRef = useRef(null);

  const createNameRef = useRef(null);
  const createDescRef = useRef(null);
  const createIconRef = useRef(null);
  const createLeaderRef = useRef(null);

  const smsMessageRef = useRef(null);
  const addMemberSearchRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const groups = useSelector(selectGroups) || [];
  const pagination = useSelector(selectGroupPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentGroup = useSelector(selectCurrentGroup);
  const groupMembers = useSelector(selectGroupMembers) || [];
  const groupMembersPagination = useSelector(selectGroupMembersPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const stats = useSelector(selectGroupStats);
  const statsSummary = useSelector(selectGroupStatsSummary);
  const loading = useSelector(selectGroupsLoading);
  const apiMessage = useSelector(selectGroupsApiMessage);
  const error = useSelector(selectGroupsError);

  // Members (for add-members modal)
  const membersList = useSelector(selectMembers) || [];
  const membersLoading = useSelector(selectMembersLoading);

  // ==================== HELPER: FORMAT DATE ====================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getGroups({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: statusFilter
        }));
        await dispatch(getGroupStats());
      } catch (err) {
        console.error('Error loading groups:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearGroupState());
    };
  }, [dispatch]);

  // Handle filters and pagination
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;
      
      dispatch(getGroups(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast, shouldShowApiToast]);

  // Fetch members when memberSearch changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: 10
      };
      if (memberSearch && memberSearch.trim().length > 0) params.search = memberSearch.trim();
      dispatch(getMembers(params));
    }, 400);

    return () => clearTimeout(timer);
  }, [memberSearch, dispatch]);

  // Keep local availableMembers in sync with global members list
  useEffect(() => {
    setAvailableMembers(membersList || []);
  }, [membersList]);

  // Update add-members modal when search or available members change
  useEffect(() => {
    if (addMembersModalOpen && addMembersGroup) {
      const group = addMembersGroup;
      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Add Members to {group.name}</div>
            <button className="modal-close" onClick={() => {
              closeModal();
              setAddMembersModalOpen(false);
              setAddMembersGroup(null);
              setMemberSearch('');
              setSelectedMembers([]);
            }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Search Members</label>
              <div className="search-box">
                <span className="search-icon"><Search size={16} /></span>
                <input 
                  type="text" 
                  placeholder="Search members by name or phone..." 
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            
            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '8px'
            }}>
              {membersLoading ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-light)' }}>Loading members...</div>
              ) : availableMembers && availableMembers.length > 0 ? (
                availableMembers.map((m) => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(m.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedMembers(prev => [...prev, m.id]);
                          else setSelectedMembers(prev => prev.filter(id => id !== m.id));
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <div className="member-row-avatar" style={{ background: '#7c3aed', width: '34px', height: '34px', fontSize: '12px' }}>{getInitials(m.full_name)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{m.full_name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{m.email || m.phone || 'No contact'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-light)' }}>No members found. Try a different search.</div>
              )}
            </div>
            
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Selected: <strong>{selectedMembers.length}</strong> members
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={() => {
              closeModal();
              setAddMembersModalOpen(false);
              setAddMembersGroup(null);
              setMemberSearch('');
              setSelectedMembers([]);
            }}>Cancel</button>
            <button 
              className="btn btn-gold btn-sm" 
              disabled={isSubmitting || selectedMembers.length === 0}
              onClick={async () => {
                setIsSubmitting(true);
                try {
                  const result = await dispatch(addMembersToGroup({
                    groupId: group.id,
                    membersData: {
                      member_ids: selectedMembers
                    }
                  })).unwrap();
                  if (result.success) {
                    closeModal();
                    setAddMembersModalOpen(false);
                    setAddMembersGroup(null);
                    setMemberSearch('');
                    setSelectedMembers([]);
                    await dispatch(getGroups({ 
                      per_page: itemsPerPage, 
                      page: currentPage,
                      search: searchTerm,
                      status: statusFilter
                    }));
                    showToastMessage(`✅ ${selectedMembers.length} members added to ${group.name}!`, 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to add members', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmitting ? (
                <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Adding...</>
              ) : (
                `Add ${selectedMembers.length} Members`
              )}
            </button>
          </div>
        </div>
      );
    }
  }, [addMembersModalOpen, addMembersGroup, memberSearch, availableMembers, membersLoading, selectedMembers, isSubmitting, dispatch, itemsPerPage, currentPage, searchTerm, statusFilter, showToastMessage, openModal, closeModal]);

  // Update create group modal when search or leader selection changes
  useEffect(() => {
    if (createGroupModalOpen) {
      // Filter members based on search term
      const leaderMembers = membersList.filter(m => {
        const fullName = (m.full_name || m.name || '').toLowerCase();
        const email = (m.email || '').toLowerCase();
        const phone = (m.phone || '').toLowerCase();
        const searchLower = createLeaderSearch.toLowerCase();
        return fullName.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
      });

      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Group</div>
            <button className="modal-close" onClick={() => {
              closeModal();
              setCreateGroupModalOpen(false);
              setCreateLeaderSearch('');
              setSelectedCreateLeader(null);
              setShowCreateLeaderDropdown(false);
            }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Group Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" placeholder="e.g. Youth, Choir…" ref={createNameRef} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input" placeholder="Short description" ref={createDescRef} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Icon (emoji)</label>
                <input className="form-input" placeholder="👥" maxLength="2" ref={createIconRef} />
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Group Leader</label>
                <div
                  style={{
                    position: 'relative',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    background: 'var(--white)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {selectedCreateLeader && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#7c3aed20',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#7c3aed'
                    }}>
                      <span>{selectedCreateLeader.full_name || selectedCreateLeader.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCreateLeader(null);
                          setCreateLeaderSearch('');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#7c3aed',
                          fontSize: '16px',
                          padding: 0
                        }}
                        title="Clear selection"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Search for a member..."
                    value={createLeaderSearch}
                    onChange={(e) => setCreateLeaderSearch(e.target.value)}
                    onFocus={() => setShowCreateLeaderDropdown(true)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      padding: 0,
                      background: 'transparent',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {showCreateLeaderDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'var(--white)',
                      border: '1px solid var(--border)',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: 'var(--shadow)'
                    }}
                  >
                    {leaderMembers.length > 0 ? (
                      leaderMembers.map((member) => (
                        <div
                          key={member.id}
                          onClick={() => {
                            setSelectedCreateLeader(member);
                            setShowCreateLeaderDropdown(false);
                            setCreateLeaderSearch('');
                          }}
                          style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid var(--border-light)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--cream)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div
                            className="member-row-avatar"
                            style={{
                              background: '#7c3aed',
                              width: '30px',
                              height: '30px',
                              fontSize: '11px'
                            }}
                          >
                            {getInitials(member.full_name || member.name)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>
                              {member.full_name || member.name}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                              {member.email || member.phone}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-light)', fontSize: '12px' }}>
                        No members found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={() => {
              closeModal();
              setCreateGroupModalOpen(false);
              setCreateLeaderSearch('');
              setSelectedCreateLeader(null);
              setShowCreateLeaderDropdown(false);
            }}>Cancel</button>
            <button 
              className="btn btn-gold btn-sm" 
              disabled={isSubmitting}
              onClick={async () => {
                const name = createNameRef.current?.value?.trim() || '';
                if (!name) {
                  showToastMessage('Please enter a group name', 'error');
                  return;
                }
                
                setIsSubmitting(true);
                try {
                  const result = await dispatch(createGroup({
                    name,
                    description: createDescRef.current?.value?.trim() || '',
                    icon: createIconRef.current?.value?.trim() || '👥',
                    leader: selectedCreateLeader ? (selectedCreateLeader.full_name || selectedCreateLeader.name) : '',
                    is_active: true
                  })).unwrap();
                  if (result.success) {
                    closeModal();
                    setCreateGroupModalOpen(false);
                    setCreateLeaderSearch('');
                    setSelectedCreateLeader(null);
                    setShowCreateLeaderDropdown(false);
                    await dispatch(getGroups({ 
                      per_page: itemsPerPage, 
                      page: currentPage,
                      search: searchTerm,
                      status: statusFilter
                    }));
                    await dispatch(getGroupStats());
                    showToastMessage(`👥 "${name}" created!`, 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to create group', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmitting ? (
                <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Creating...</>
              ) : (
                'Create Group'
              )}
            </button>
          </div>
        </div>
      );
    }
  }, [createGroupModalOpen, createLeaderSearch, selectedCreateLeader, showCreateLeaderDropdown, membersList, isSubmitting, dispatch, itemsPerPage, currentPage, searchTerm, statusFilter, showToastMessage, openModal, closeModal]);

  // Update edit group modal when search or leader selection changes
  useEffect(() => {
    if (editGroupModalOpen && editingGroup) {
      const group = editingGroup;
      
      // Auto-select existing leader if not already selected
      if (!selectedEditLeader && group.leader && membersList.length > 0) {
        const existingLeader = membersList.find(m => 
          (m.full_name || m.name) === group.leader
        );
        if (existingLeader) {
          setSelectedEditLeader(existingLeader);
        }
      }
      
      // Filter members based on search term
      const leaderMembers = membersList.filter(m => {
        const fullName = (m.full_name || m.name || '').toLowerCase();
        const email = (m.email || '').toLowerCase();
        const phone = (m.phone || '').toLowerCase();
        const searchLower = editLeaderSearch.toLowerCase();
        return fullName.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
      });

      openModal(
        <div>
          <div className="modal-header">
            <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Group: {group.name}</div>
            <button className="modal-close" onClick={() => {
              closeModal();
              setEditGroupModalOpen(false);
              setEditingGroup(null);
              setEditLeaderSearch('');
              setSelectedEditLeader(null);
              setShowEditLeaderDropdown(false);
            }}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Group Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="form-input" ref={editNameRef} defaultValue={group.name || ''} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input" ref={editDescRef} defaultValue={group.description || ''} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Icon (emoji)</label>
                <input className="form-input" maxLength="2" ref={editIconRef} defaultValue={group.icon || '👥'} />
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Group Leader</label>
                <div
                  style={{
                    position: 'relative',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    background: 'var(--white)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {selectedEditLeader && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: '#7c3aed20',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#7c3aed'
                    }}>
                      <span>{selectedEditLeader.full_name || selectedEditLeader.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEditLeader(null);
                          setEditLeaderSearch('');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#7c3aed',
                          fontSize: '16px',
                          padding: 0
                        }}
                        title="Clear selection"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Search for a member..."
                    value={editLeaderSearch}
                    onChange={(e) => setEditLeaderSearch(e.target.value)}
                    onFocus={() => setShowEditLeaderDropdown(true)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      padding: 0,
                      background: 'transparent',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {showEditLeaderDropdown && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'var(--white)',
                      border: '1px solid var(--border)',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: 'var(--shadow)'
                    }}
                  >
                    {leaderMembers.length > 0 ? (
                      leaderMembers.map((member) => (
                        <div
                          key={member.id}
                          onClick={() => {
                            setSelectedEditLeader(member);
                            setShowEditLeaderDropdown(false);
                            setEditLeaderSearch('');
                          }}
                          style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid var(--border-light)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--cream)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div
                            className="member-row-avatar"
                            style={{
                              background: '#7c3aed',
                              width: '30px',
                              height: '30px',
                              fontSize: '11px'
                            }}
                          >
                            {getInitials(member.full_name || member.name)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: '500' }}>
                              {member.full_name || member.name}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                              {member.email || member.phone}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-light)', fontSize: '12px' }}>
                        No members found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>Active</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Group is active and available</div>
              </div>
              <label className="toggle">
                <input type="checkbox" ref={editActiveRef} defaultChecked={group.is_active !== false} />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={() => {
              closeModal();
              setEditGroupModalOpen(false);
              setEditingGroup(null);
              setEditLeaderSearch('');
              setSelectedEditLeader(null);
              setShowEditLeaderDropdown(false);
            }}>Cancel</button>
            <button 
              className="btn btn-gold btn-sm" 
              disabled={isSubmitting}
              onClick={async () => {
                const name = editNameRef.current?.value?.trim() || '';
                if (!name) {
                  showToastMessage('Please enter a group name', 'error');
                  return;
                }
                
                setIsSubmitting(true);
                try {
                  const result = await dispatch(updateGroup({
                    id: group.id,
                    data: {
                      name,
                      description: editDescRef.current?.value?.trim() || '',
                      icon: editIconRef.current?.value?.trim() || '👥',
                      leader: selectedEditLeader ? (selectedEditLeader.full_name || selectedEditLeader.name) : editNameRef.current?.value?.trim() || '',
                      is_active: editActiveRef.current?.checked !== false
                    }
                  })).unwrap();
                  if (result.success) {
                    closeModal();
                    setEditGroupModalOpen(false);
                    setEditingGroup(null);
                    setEditLeaderSearch('');
                    setSelectedEditLeader(null);
                    setShowEditLeaderDropdown(false);
                    await dispatch(getGroups({ 
                      per_page: itemsPerPage, 
                      page: currentPage,
                      search: searchTerm,
                      status: statusFilter
                    }));
                    await dispatch(getGroupStats());
                    showToastMessage(`✅ "${name}" updated successfully!`, 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to update group', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmitting ? (
                <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
              ) : (
                <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>
              )}
            </button>
          </div>
        </div>
      );
    }
  }, [editGroupModalOpen, editingGroup, editLeaderSearch, selectedEditLeader, showEditLeaderDropdown, membersList, isSubmitting, dispatch, itemsPerPage, currentPage, searchTerm, statusFilter, showToastMessage, openModal, closeModal]);

  // ==================== PAGINATION ====================

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== FILTER GROUPS ====================
  const filteredGroups = groups.filter(g => {
    const matchesSearch = g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          g.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          g.leader?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || 
                          (statusFilter === 'active' && g.is_active) ||
                          (statusFilter === 'inactive' && !g.is_active);
    return matchesSearch && matchesStatus;
  });

  // ==================== EXPORT FUNCTIONS ====================

  const openExportModal = (exportType = 'all', group = null) => {
    let data = [];
    let title = 'Groups Report';
    let filename = 'groups-report';

    if (exportType === 'single' && group) {
      data = [{
        'Group Name': group.name,
        'Description': group.description || 'N/A',
        'Members': group.member_count || 0,
        'Leader': group.leader || 'N/A',
        'Status': group.is_active ? 'Active' : 'Inactive',
        'Created': formatDate(group.created_at)
      }];
      title = `Group Report: ${group.name}`;
      filename = `group-${group.id}-${group.name.substring(0, 30)}`;
    } else if (exportType === 'active') {
      data = groups
        .filter(g => g.is_active)
        .map(g => ({
          'Group Name': g.name,
          'Description': g.description || 'N/A',
          'Members': g.member_count || 0,
          'Leader': g.leader || 'N/A',
          'Status': 'Active'
        }));
      title = 'Active Groups Report';
      filename = 'active-groups';
    } else if (exportType === 'summary') {
      const totalMembers = groups.reduce((a, b) => a + (b.member_count || 0), 0);
      const activeGroups = groups.filter(g => g.is_active).length;
      
      data = groups.map(g => ({
        'Group Name': g.name,
        'Members': g.member_count || 0,
        'Leader': g.leader || 'N/A',
        'Status': g.is_active ? 'Active' : 'Inactive'
      }));
      
      data.push({
        'Group Name': '=== TOTALS ===',
        'Members': totalMembers,
        'Leader': `${activeGroups} active groups`,
        'Status': ''
      });
      
      title = 'Groups Summary Report';
      filename = 'groups-summary';
    } else {
      data = groups.map(g => ({
        'Group ID': g.id,
        'Group Name': g.name,
        'Description': g.description || 'N/A',
        'Members': g.member_count || 0,
        'Leader': g.leader || 'N/A',
        'Status': g.is_active ? 'Active' : 'Inactive',
        'Created': formatDate(g.created_at),
        'Icon': g.icon || '👥'
      }));
      title = 'All Groups Report';
      filename = 'all-groups';
    }

    if (data.length === 0) {
      showToastMessage('No data to export', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    switch (format) {
                      case 'CSV':
                        exportToCSV(data, getExportFilename(filename));
                        break;
                      case 'JSON':
                        exportToJSON(data, getExportFilename(filename));
                        break;
                      case 'Excel':
                        exportToExcel(data, getExportFilename(filename));
                        break;
                      case 'PDF':
                        exportToPDF(data, title, getExportFilename(filename));
                        break;
                      default:
                        showToastMessage('Export format not supported', 'error');
                        return;
                    }
                    showToastMessage(`📥 ${data.length} records exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{data.length} records will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  const handleExportAll = () => {
    if (groups.length === 0) {
      showToastMessage('No groups to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportGroup = (group) => {
    openExportModal('single', group);
  };

  const handleExportActive = () => {
    const active = groups.filter(g => g.is_active);
    if (active.length === 0) {
      showToastMessage('No active groups to export', 'info');
      return;
    }
    openExportModal('active');
  };

  const handleExportSummary = () => {
    if (groups.length === 0) {
      showToastMessage('No groups to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  // ==================== GROUP CRUD ====================

  const toggleGroup = async (id, active) => {
    try {
      await dispatch(toggleGroupStatus(id)).unwrap();
      await dispatch(getGroups({ 
        per_page: itemsPerPage, 
        page: currentPage,
        search: searchTerm,
        status: statusFilter
      }));
      await dispatch(getGroupStats());
      showToastMessage(`Group ${active ? 'activated' : 'paused'} successfully`, active ? 'success' : 'info');
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle group status', 'error');
    }
  };

  const openCreateGroup = () => {
    // Reset create group states
    setCreateLeaderSearch('');
    setSelectedCreateLeader(null);
    setShowCreateLeaderDropdown(false);
    
    setTimeout(() => {
      if (createNameRef.current) createNameRef.current.value = '';
      if (createDescRef.current) createDescRef.current.value = '';
      if (createIconRef.current) createIconRef.current.value = '👥';
    }, 0);

    // Load members for dropdown
    dispatch(getMembers({ per_page: 50 }));
    
    // Set modal open which will trigger the effect
    setCreateGroupModalOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setEditLeaderSearch('');
    setSelectedEditLeader(null);
    setShowEditLeaderDropdown(false);
    
    setTimeout(() => {
      if (editNameRef.current) editNameRef.current.value = group.name || '';
      if (editDescRef.current) editDescRef.current.value = group.description || '';
      if (editIconRef.current) editIconRef.current.value = group.icon || '👥';
      if (editActiveRef.current) editActiveRef.current.checked = group.is_active !== false;
    }, 50);

    // Load members for dropdown
    dispatch(getMembers({ per_page: 50 }));
    
    // Set modal open which will trigger the effect
    setEditGroupModalOpen(true);
  };

  const handleDeleteGroup = (group) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Group</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{group.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will permanently remove the group and all member associations.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteGroup(group.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getGroups({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    status: statusFilter
                  }));
                  await dispatch(getGroupStats());
                  showToastMessage(`🗑️ "${group.name}" deleted`, 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete group', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Group'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleDuplicateGroup = async (group) => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(duplicateGroup(group.id)).unwrap();
      if (result.success) {
        await dispatch(getGroups({ 
          per_page: itemsPerPage, 
          page: currentPage,
          search: searchTerm,
          status: statusFilter
        }));
        await dispatch(getGroupStats());
        showToastMessage(`📋 "${result.group.name}" created`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to duplicate group', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== GROUP MEMBER MANAGEMENT ====================

  const openAddMembersModal = (group) => {
    setSelectedMembers([]);
    setMemberSearch('');
    setAddMembersGroup(group);
    setAddMembersModalOpen(true);
    // preload a small list of members
    dispatch(getMembers({ per_page: 10 }));
  };

  const handleRemoveMember = async (groupId, memberId, memberName) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Remove Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to remove <strong>{memberName}</strong> from this group?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--amber)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will remove the member from the group.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                // Updated: use groupId and memberId parameters
                const result = await dispatch(removeMemberFromGroup({
                  groupId: groupId,
                  memberId: memberId
                })).unwrap();
                if (result.success) {
                  closeModal();
                  await dispatch(getGroup(groupId));
                  showToastMessage(`✅ Member removed from group`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to remove member', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Removing...</>
            ) : (
              'Remove Member'
            )}
          </button>
        </div>
      </div>
    );
  };

  const openGroupDetail = async (group) => {
    try {
      await dispatch(getGroup(group.id));
      
      const members = groupMembers || [];
      const membersPagination = groupMembersPagination || {};
      
      openModal(
        <div>
          <div className="modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: `${group.color || '#4f46e5'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>{group.icon || '👥'}</div>
              <div>
                <div className="modal-title">{group.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                  {group.member_count || 0} members · {group.leader || 'N/A'}
                  <span className={`badge ${group.is_active ? 'badge-green' : 'badge-red'}`} style={{ marginLeft: '8px', fontSize: '10px' }}>
                    {group.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                Members ({members.length || 0})
              </div>
              <button 
                className="btn btn-gold btn-sm" 
                onClick={() => {
                  closeModal();
                  setTimeout(() => openAddMembersModal(group), 100);
                }}
              >
                + Add Members
              </button>
            </div>
            
            {members.length > 0 ? (
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {members.map((m, index) => {
                  const colors = ['#4f46e5', '#059669', '#d97706', '#7c3aed', '#0891b2', '#dc2626'];
                  const color = colors[index % colors.length];
                  return (
                    <div key={m.id || index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                      <div className="member-row-avatar" style={{ background: color, width: '30px', height: '30px', fontSize: '11px' }}>{getInitials(m.full_name || m.name)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>{m.full_name || m.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{m.phone || 'No phone'}</div>
                      </div>
                      <span className={`badge ${m.status === 'Active' || m.pivot?.status === 'active' ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: '10px' }}>
                        {m.status || m.pivot?.status || 'Active'}
                      </span>
                      <button 
                        className="btn btn-ghost btn-sm btn-icon" 
                        onClick={() => handleRemoveMember(group.id, m.id, m.full_name || m.name)}
                        style={{ color: 'var(--red)' }}
                        title="Remove member"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-light)' }}>
                No members in this group.
              </div>
            )}
            
            {membersPagination && membersPagination.total > 10 && (
              <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
                Showing {members.length} of {membersPagination.total} members
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExportGroup(group)}><Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export</button>
            <button className="btn btn-gold btn-sm" onClick={() => {
              closeModal();
              setTimeout(() => handleSendGroupSms(group), 100);
            }}><Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />SMS Group</button>
            <button className="btn btn-ghost btn-sm" onClick={() => {
              closeModal();
              setTimeout(() => handleEditGroup(group), 100);
            }}><Pencil size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Edit</button>
          </div>
        </div>
      );
    } catch (err) {
      showToastMessage(err.message || 'Failed to load group details', 'error');
    }
  };

  // ==================== SEND SMS TO GROUP ====================

  const handleSendGroupSms = (group) => {
    setTimeout(() => {
      if (smsMessageRef.current) {
        smsMessageRef.current.value = `Hello {FirstName}, this message is for the ${group.name} group. — Grace Chapel`;
      }
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Send SMS to {group.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
              Send SMS to <strong>{group.member_count || 0}</strong> members of <strong>{group.name}</strong>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea className="form-textarea" rows="4" ref={smsMessageRef} defaultValue={`Hello {FirstName}, this message is for the ${group.name} group. — Grace Chapel`} />
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
            <Lightbulb size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Use {'{FirstName}'} for personalisation
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const message = smsMessageRef.current?.value?.trim() || '';
              if (!message) {
                showToastMessage('Please enter a message', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(sendSmsToGroup({
                  id: group.id,
                  message: message,
                  // sender_id: 'CHURCH'
                })).unwrap();
                if (result.success) {
                  closeModal();
                  showToastMessage(`📨 SMS sent to ${group.name}! (${result.sent || 0} sent)`, 'success');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to send SMS', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Sending...</>
            ) : (
              'Send SMS'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = statsSummary || stats || { 
    total: 0, 
    active: 0, 
    inactive: 0, 
    total_members: 0,
    largest_group: null
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <Users size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Organise members into groups for targeted SMS communication.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportSummary}
            title="Export groups summary"
            disabled={loading}
          >
            <ClipboardList size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportActive}
            title="Export active groups"
            disabled={loading}
          >
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Active
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all groups"
            disabled={loading}
          >
            <Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export All
          </button>
          <button className="btn btn-gold btn-sm" onClick={openCreateGroup} disabled={loading}>
            + Create Group
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Groups</div>
          <div className="stat-value">{statsData.total || 0}</div>
          <div className="stat-change up">all groups</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Active Groups</div>
          <div className="stat-value">{statsData.active || 0}</div>
          <div className="stat-change up">running</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Grouped</div>
          <div className="stat-value">{statsData.total_members || 0}</div>
          <div className="stat-change up">members</div>
          <div className="stat-icon"><BarChart3 size={20} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">Largest Group</div>
          <div className="stat-value">{statsData.largest_group?.name || 'N/A'}</div>
          <div className="stat-change up">{statsData.largest_group?.member_count || 0} members</div>
          <div className="stat-icon"><Trophy size={20} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={16} /></span>
            <input 
              type="text" 
              placeholder="Search groups..." 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search by name, description, or leader"
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by status"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {filteredGroups.length} groups found
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {loading && filteredGroups.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0' }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
            <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading groups...</div>
          </div>
        ) : filteredGroups.length > 0 ? (
          filteredGroups.map(g => {
            const color = g.color || '#4f46e5';
            return (
              <div key={g.id} className="card" style={{ borderTop: `3px solid ${color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>{g.icon || '👥'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{g.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{g.description || 'No description'}</div>
                  </div>
                  <label className="toggle" title={g.is_active ? 'Click to pause' : 'Click to activate'}>
                    <input type="checkbox" checked={g.is_active !== false} onChange={(e) => toggleGroup(g.id, e.target.checked)} disabled={loading} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '16px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{g.member_count || 0}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Members</div>
                  </div>
                  <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Leader</div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginTop: '2px' }}>{g.leader || 'N/A'}</div>
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '10px' }}><Calendar size={11} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Created {formatDate(g.created_at)}</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-gold btn-sm"
                    style={{ flex: 1 }}
                    onClick={() => handleSendGroupSms(g)}
                    title={`Send SMS to ${g.name}`}
                    disabled={loading}
                  >
                    <Send size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />SMS Group
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => openGroupDetail(g)}
                    title={`View ${g.name} details`}
                    disabled={loading}
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => openAddMembersModal(g)}
                    title={`Add members to ${g.name}`}
                    disabled={loading}
                  >
                    <Plus size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleExportGroup(g)}
                    title={`Export ${g.name} data`}
                    disabled={loading}
                  >
                    <Download size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleEditGroup(g)}
                    title={`Edit ${g.name}`}
                    disabled={loading}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleDuplicateGroup(g)}
                    title={`Duplicate ${g.name}`}
                    disabled={loading || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    onClick={() => handleDeleteGroup(g)}
                    title={`Delete ${g.name}`}
                    style={{ color: 'var(--red)' }}
                    disabled={loading}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state">
              <div className="empty-icon"><Users size={32} strokeWidth={1.5} /></div>
              <div className="empty-title">No groups found</div>
              <div className="empty-text">Try adjusting your search or filters</div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '20px',
          padding: '16px',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{((currentPageNum - 1) * perPage) + 1}</strong> to <strong>{Math.min(currentPageNum * perPage, totalItems)}</strong> of <strong>{totalItems}</strong> groups
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                title="Items per page"
                disabled={loading}
              >
                <option value={3}>3</option>
                <option value={6}>6</option>
                <option value={9}>9</option>
                <option value={12}>12</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={goToPreviousPage}
                disabled={currentPageNum === 1 || loading}
                style={{ 
                  opacity: currentPageNum === 1 ? 0.5 : 1, 
                  cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                }}
                title="Previous page"
              >
                ← Prev
              </button>
              
              {getPageNumbers().map(page => (
                <button 
                  key={page}
                  className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  title={`Go to page ${page}`}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="btn btn-ghost btn-sm"
                onClick={goToNextPage}
                disabled={currentPageNum === totalPages || loading}
                style={{ 
                  opacity: currentPageNum === totalPages ? 0.5 : 1, 
                  cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                }}
                title="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .groups-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .groups-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        .member-row-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          flex-shrink: 0;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
      `}</style>
    </>
  );
};

export default GroupsManager;